import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Row, Col, Badge } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { X } from 'react-feather';
import { ServerPath } from '../../Constant';
import { _axios } from '../../utils/axios';

const inputStyle = {
    height: '46px',
    border: '1px solid #E2E8F0',
    backgroundColor: '#FFFFFF',
    borderRadius: '6px',
};

const labelStyle = {
    color: '#4A5568',
    fontWeight: '500',
    fontSize: '16px',
};

const streamTypeBadge = (type) => {
    const config = {
        youtube: { label: 'YouTube', bg: '#FF0000' },
        cctv: { label: 'Internal', bg: '#00BCD4' },
        cctv_external: { label: 'External', bg: '#FF9800' },
    }[type] || { label: type, bg: '#9E9E9E' };
    return (
        <span style={{
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: '12px',
            backgroundColor: config.bg,
            color: '#fff',
            fontSize: '10px',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.3px',
        }}>
            {config.label}
        </span>
    );
};

const streamStatusBadge = (status) => {
    const config = {
        draft: { label: 'Draft', bg: '#9E9E9E' },
        live_now: { label: 'Live Now', bg: '#4CAF50' },
        processing: { label: 'Processing', bg: '#FF9800' },
        recently_live: { label: 'Recently Live', bg: '#2196F3' },
        archived: { label: 'Archived', bg: '#424242' },
    }[status] || { label: status || 'Unknown', bg: '#9E9E9E' };
    return (
        <span style={{
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: '12px',
            backgroundColor: config.bg,
            color: '#fff',
            fontSize: '10px',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.3px',
            marginLeft: '4px',
        }}>
            {config.label}
        </span>
    );
};

const StreamPickerOption = ({ stream, isSelected, onToggle }) => (
    <div
        onClick={() => onToggle(stream)}
        style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '10px 12px',
            borderRadius: '8px',
            border: `1px solid ${isSelected ? '#5662F6' : '#E2E8F0'}`,
            background: isSelected ? '#F0F1FF' : '#FFFFFF',
            cursor: 'pointer',
            transition: 'all 0.15s ease',
        }}
    >
        <Form.Check
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggle(stream)}
            onClick={(e) => e.stopPropagation()}
            style={{ margin: 0 }}
        />
        {stream.thumbnailUrl ? (
            <img
                src={stream.thumbnailUrl}
                alt={stream.streamTitle}
                style={{ width: '56px', height: '40px', objectFit: 'cover', borderRadius: '4px', flexShrink: 0 }}
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
        ) : (
            <div style={{ width: '56px', height: '40px', borderRadius: '4px', background: '#F1F5F9', flexShrink: 0 }} />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
                fontSize: '14px',
                fontWeight: '500',
                color: '#1F2937',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            }}>
                {stream.streamTitle}
            </div>
            <div style={{ marginTop: '4px' }}>
                {streamTypeBadge(stream.streamType)}
                {stream.streamStatus && streamStatusBadge(stream.streamStatus)}
            </div>
        </div>
    </div>
);

const SelectedChip = ({ stream, onRemove }) => (
    <span style={{
        backgroundColor: '#5662F6',
        color: 'white',
        padding: '6px 10px',
        borderRadius: '6px',
        fontSize: '13px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        maxWidth: '100%',
    }}>
        <span style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '200px',
        }}>
            {stream.streamTitle}
        </span>
        <button
            type="button"
            onClick={() => onRemove(stream.id)}
            className="border-0 bg-transparent text-white p-0"
            style={{ lineHeight: 1, cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            aria-label={`Remove ${stream.streamTitle}`}
        >
            <X size={14} />
        </button>
    </span>
);

const EditMultiView = ({ show, onHide, groupId }) => {
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [title, setTitle] = useState('');
    const [status, setStatus] = useState('active');
    const [eligibleStreams, setEligibleStreams] = useState([]);
    const [selectedStreams, setSelectedStreams] = useState([]);
    const [searchValue, setSearchValue] = useState('');
    const [groupMeta, setGroupMeta] = useState(null);

    useEffect(() => {
        if (!groupId) return;
        loadAll();
    }, [groupId]);

    const loadAll = async () => {
        try {
            setLoading(true);
            const [groupRes, streamsRes] = await Promise.all([
                _axios.get(`${ServerPath}/live-darshan/multi-view/${groupId}`),
                _axios.get(`${ServerPath}/live-darshan/multi-view/eligible-streams?group_id=${groupId}`),
            ]);

            if (groupRes.data.success !== 1) {
                toast.error(groupRes.data.message || 'Failed to load Multi View group');
                onHide();
                return;
            }

            const group = groupRes.data.data;
            setGroupMeta(group);
            setTitle(group.title || '');
            setStatus(group.status || 'active');

            const memberStreams = (group.streams || []).map(s => ({
                id: s.id,
                streamTitle: s.streamTitle,
                thumbnailUrl: s.thumbnailUrl,
                streamType: s.streamType,
                streamStatus: s.streamStatus,
                orientation: s.orientation,
                enableMultiView: s.enableMultiView,
                addedAt: s.addedAt,
            }));
            setSelectedStreams(memberStreams);

            if (streamsRes.data.success === 1) {
                setEligibleStreams(streamsRes.data.data || []);
            } else {
                toast.error(streamsRes.data.message || 'Failed to load eligible streams');
            }
        } catch (error) {
            console.error('Error loading Multi View group:', error);
            const status = error.response?.status;
            const msg = error.response?.data?.message;
            if (status === 404) {
                toast.error(msg || 'Multi view group not found');
                onHide();
            } else {
                toast.error(msg || 'Error loading Multi View group');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleToggleStream = (stream) => {
        setSelectedStreams(prev => {
            const exists = prev.some(s => s.id === stream.id);
            if (exists) return prev.filter(s => s.id !== stream.id);
            return [...prev, stream];
        });
    };

    const handleRemoveSelected = (id) => {
        setSelectedStreams(prev => prev.filter(s => s.id !== id));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;

        const trimmedTitle = title.trim();
        if (!trimmedTitle) {
            toast.error('Title is required');
            return;
        }
        if (trimmedTitle.length > 255) {
            toast.error('Title cannot exceed 255 characters');
            return;
        }
        if (selectedStreams.length < 2) {
            toast.error('At least 2 streams are required');
            return;
        }

        try {
            setIsSubmitting(true);
            const payload = {
                group_id: groupId,
                title: trimmedTitle,
                status,
                stream_ids: selectedStreams.map(s => s.id),
            };
            const response = await _axios.post(`${ServerPath}/live-darshan/multi-view/update`, payload);
            if (response.data.success === 1) {
                toast.success('Multi View group updated');
                setTimeout(() => onHide(), 800);
            } else {
                toast.error(response.data.message || 'Failed to update Multi View group');
                setIsSubmitting(false);
            }
        } catch (error) {
            console.error('Error updating Multi View group:', error);
            const httpStatus = error.response?.status;
            const msg = error.response?.data?.message;
            if (httpStatus === 404) {
                toast.error(msg || 'Multi view group not found');
            } else if (httpStatus === 400 && msg) {
                toast.error(msg);
            } else if (httpStatus >= 500) {
                toast.error('Something went wrong');
            } else {
                toast.error(msg || 'Error updating Multi View group');
            }
            setIsSubmitting(false);
        }
    };

    const filteredStreams = eligibleStreams.filter(s =>
        (s.streamTitle || '').toLowerCase().includes(searchValue.toLowerCase())
    );

    return (
        <Modal show={show} onHide={onHide} size="lg" centered style={{ borderRadius: '10px' }}>
            <div style={{ borderRadius: '10px' }}>
                <Modal.Header closeButton style={{ borderBottom: '1px solid #E2E8F0', padding: '20px 24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <Modal.Title style={{ fontWeight: '600', fontSize: '22px' }}>Edit Multi View</Modal.Title>
                        {groupMeta?.status && (
                            <Badge bg={groupMeta.status === 'active' ? 'success' : 'secondary'}>
                                {groupMeta.status === 'active' ? 'Active' : 'Inactive'}
                            </Badge>
                        )}
                    </div>
                </Modal.Header>

                <Modal.Body style={{ padding: '24px' }}>
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '60px', color: '#A0AEC0' }}>Loading...</div>
                    ) : (
                        <Form>
                            <Row className="mb-4">
                                <Col md={8}>
                                    <Form.Group>
                                        <Form.Label className="mb-2" style={labelStyle}>
                                            Multi View Title <span className="text-danger">*</span>
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={title}
                                            onChange={(e) => setTitle(e.target.value)}
                                            placeholder="e.g., Pandharpur Vitthal — All Cameras"
                                            maxLength={255}
                                            style={inputStyle}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label className="mb-2" style={labelStyle}>Status</Form.Label>
                                        <Form.Select
                                            value={status}
                                            onChange={(e) => setStatus(e.target.value)}
                                            style={inputStyle}
                                        >
                                            <option value="active">Active</option>
                                            <option value="inactive">Inactive</option>
                                        </Form.Select>
                                        <Form.Text className="text-muted">
                                            Inactive hides Multi Darshan in the app but preserves the configuration.
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label className="mb-2" style={labelStyle}>
                                            Select Multi View Live Streams <span className="text-danger">*</span>
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={searchValue}
                                            onChange={(e) => setSearchValue(e.target.value)}
                                            placeholder="Search streams..."
                                            style={{ ...inputStyle, marginBottom: '8px' }}
                                        />
                                        <div style={{
                                            border: '1px solid #E2E8F0',
                                            borderRadius: '6px',
                                            padding: '8px',
                                            height: '320px',
                                            overflowY: 'auto',
                                            background: '#FAFAFA',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '6px',
                                        }}>
                                            {filteredStreams.length === 0 && (
                                                <div style={{ textAlign: 'center', color: '#A0AEC0', padding: '20px', fontSize: '13px' }}>
                                                    No streams available.
                                                </div>
                                            )}
                                            {filteredStreams.map(stream => (
                                                <StreamPickerOption
                                                    key={stream.id}
                                                    stream={stream}
                                                    isSelected={selectedStreams.some(s => s.id === stream.id)}
                                                    onToggle={handleToggleStream}
                                                />
                                            ))}
                                        </div>
                                        <Form.Text className="text-muted">
                                            Streams appear in the order they were added (newest first). To move a stream to the front, remove and re-add it.
                                        </Form.Text>
                                    </Form.Group>
                                </Col>

                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label className="mb-2" style={labelStyle}>
                                            Selected ({selectedStreams.length})
                                        </Form.Label>
                                        <div style={{
                                            border: '1px solid #E2E8F0',
                                            borderRadius: '6px',
                                            padding: '12px',
                                            minHeight: '370px',
                                            maxHeight: '370px',
                                            overflowY: 'auto',
                                            background: '#FAFAFA',
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: '8px',
                                            alignContent: 'flex-start',
                                        }}>
                                            {selectedStreams.length === 0 && (
                                                <span style={{ color: '#A0AEC0', fontSize: '13px' }}>
                                                    No streams selected
                                                </span>
                                            )}
                                            {selectedStreams.map(stream => (
                                                <SelectedChip
                                                    key={stream.id}
                                                    stream={stream}
                                                    onRemove={handleRemoveSelected}
                                                />
                                            ))}
                                        </div>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Form>
                    )}
                </Modal.Body>

                <div style={{ padding: '0 24px 24px 0', display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        variant="secondary"
                        onClick={onHide}
                        className="me-2"
                        style={{ borderRadius: '6px', padding: '10px 24px', fontWeight: '500', fontSize: '16px' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleSubmit}
                        disabled={isSubmitting || loading}
                        style={{
                            backgroundColor: '#5662F6',
                            borderColor: '#5662F6',
                            borderRadius: '6px',
                            padding: '10px 40px',
                            fontWeight: '500',
                            fontSize: '16px',
                        }}
                    >
                        {isSubmitting ? 'Saving...' : 'Save'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default EditMultiView;
